import { RefObject, useEffect } from "react";

import type { EditorView } from "prosemirror-view";

import { SpellChecker, getSpellChecker } from "./dictionary";
import { tokenizeWords } from "./tokenize";

/**
 * App-side spell-check overlay for the Report Builder editor.
 *
 * The vendored docx-editor renders ProseMirror off-screen and paints the visible
 * pages as a separate DOM projection, so PM decorations and native browser
 * spell-check never reach the visible text. Instead we watch the visible page
 * DOM and wrap misspelled words in a red-squiggle `<span>` ourselves.
 *
 * The visible fragments carry `data-pm-start` (the paragraph's PM node position),
 * so we (a) only walk painted content — never the hidden PM DOM — and (b) can map
 * a clicked squiggle back to a PM range (`pmStart + 1 + charOffset`) to apply a
 * correction via a transaction, the same position convention the Refine feature
 * uses. Checking is on-device (en-AU `nspell`, [[dictionary]]) — no report text
 * leaves the browser. Re-applied (debounced) on every repaint; the observer is
 * detached while we mutate so our own spans don't retrigger it.
 */
const WRAP_CLASS = "spellcheck-error";
const SQUIGGLE_STYLE =
  "text-decoration: underline wavy #d64545; text-decoration-skip-ink: none; cursor: pointer;";

/**
 * Character offset of `span`'s text within its containing paragraph element —
 * the sum of all text before it. Maps to the PM content offset for plain text.
 * Exported for testing.
 */
export function offsetWithinParagraph(paraEl: Element, span: HTMLElement): number {
  const walker = document.createTreeWalker(paraEl, NodeFilter.SHOW_TEXT);
  let offset = 0;
  let node = walker.nextNode();
  while (node) {
    if (span.contains(node)) break;
    offset += (node.nodeValue ?? "").length;
    node = walker.nextNode();
  }
  return offset;
}

/**
 * Map a squiggle span back to the ProseMirror range of its word, using the
 * painted paragraph fragment's `data-pm-start` (the paragraph node position):
 * `from = pmStart + 1 + charOffset` (the Refine feature's convention). Returns
 * null if the span isn't inside a painted fragment. Exported for testing. The
 * caller still verifies the range holds the word before editing (drift guard).
 */
export function wordPmRange(
  span: HTMLElement
): { from: number; to: number; word: string } | null {
  const paraEl = span.closest("[data-pm-start]") as HTMLElement | null;
  const word = span.dataset.word ?? span.textContent ?? "";
  if (!paraEl || !word) return null;
  const pmStart = Number(paraEl.dataset.pmStart);
  if (Number.isNaN(pmStart)) return null;
  const from = pmStart + 1 + offsetWithinParagraph(paraEl, span);
  return { from, to: from + word.length, word };
}

export function useSpellcheckOverlay(
  containerRef: RefObject<HTMLElement | null>,
  viewRef: RefObject<EditorView | null>
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let checker: SpellChecker | null = null;
    let observer: MutationObserver | null = null;
    let timer: number | undefined;
    let disposed = false;

    const unwrapAll = () => {
      container.querySelectorAll(`span.${WRAP_CLASS}`).forEach((span) => {
        const parent = span.parentNode;
        if (!parent) return;
        parent.replaceChild(document.createTextNode(span.textContent ?? ""), span);
        parent.normalize();
      });
    };

    const wrapTextNode = (textNode: Text) => {
      const text = textNode.nodeValue ?? "";
      // Template merge fields (e.g. <<Client_SalLastName>>) aren't real words —
      // exclude any token inside a << … >> placeholder.
      const mergeRanges: Array<[number, number]> = [];
      for (const m of text.matchAll(/<<[^>]*>>/g)) {
        const s = m.index ?? 0;
        mergeRanges.push([s, s + m[0].length]);
      }
      const inMergeField = (start: number, end: number) =>
        mergeRanges.some(([s, e]) => start < e && end > s);
      const bad = tokenizeWords(text).filter(
        (t) => checker && !checker.isCorrect(t.word) && !inMergeField(t.start, t.end)
      );
      if (bad.length === 0) return;
      const frag = document.createDocumentFragment();
      let last = 0;
      for (const t of bad) {
        if (t.start > last)
          frag.appendChild(document.createTextNode(text.slice(last, t.start)));
        const span = document.createElement("span");
        span.className = WRAP_CLASS;
        span.setAttribute("style", SQUIGGLE_STYLE);
        span.dataset.word = t.word;
        span.textContent = text.slice(t.start, t.end);
        frag.appendChild(span);
        last = t.end;
      }
      if (last < text.length)
        frag.appendChild(document.createTextNode(text.slice(last)));
      textNode.parentNode?.replaceChild(frag, textNode);
    };

    const apply = () => {
      if (disposed || !checker) return;
      observer?.disconnect();
      try {
        unwrapAll();
        // Only painted fragments (they carry data-pm-start) — never the hidden
        // off-screen ProseMirror DOM.
        container.querySelectorAll("[data-pm-start]").forEach((frag) => {
          const walker = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT);
          const nodes: Text[] = [];
          let n = walker.nextNode();
          while (n) {
            nodes.push(n as Text);
            n = walker.nextNode();
          }
          nodes.forEach(wrapTextNode);
        });
      } catch (err) {
        console.error("Spell-check overlay failed:", err);
      } finally {
        observer?.observe(container, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(apply, 300);
    };

    // ── click-to-fix ─────────────────────────────────────────────────────────
    const applyFix = (span: HTMLElement, replacement: string) => {
      const view = viewRef.current;
      if (!view) return;
      const range = wordPmRange(span);
      if (!range) return;
      // Drift guard: the offset math assumes plain-text paragraphs, so it can be
      // wrong when a paragraph contains inline atoms or is split across a page
      // break. Only replace if the computed PM range actually holds the flagged
      // word — otherwise no-op rather than silently overwriting the wrong text.
      if (view.state.doc.textBetween(range.from, range.to) !== range.word) return;
      try {
        view.dispatch(view.state.tr.insertText(replacement, range.from, range.to));
      } catch (err) {
        console.error("Spell-check fix failed:", err);
      }
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const span = target?.closest?.(`span.${WRAP_CLASS}`) as HTMLElement | null;
      if (!span || !checker) return;
      // Our popover handles this word — don't let the editor place a caret.
      e.preventDefault();
      e.stopPropagation();
      const word = span.dataset.word ?? span.textContent ?? "";
      showPopover(span, checker.suggest(word), {
        onPick: (s) => applyFix(span, s),
        onIgnore: () => {
          checker?.ignore(word);
          apply();
        },
      });
    };
    container.addEventListener("mousedown", onClick, true);

    getSpellChecker().then((c) => {
      if (disposed) return;
      checker = c;
      if (!c) return;
      observer = new MutationObserver(schedule);
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      schedule();
    });

    return () => {
      disposed = true;
      window.clearTimeout(timer);
      observer?.disconnect();
      container.removeEventListener("mousedown", onClick, true);
      closePopover();
    };
  }, [containerRef, viewRef]);
}

// ── suggestion popover (vanilla DOM) ─────────────────────────────────────────
let popoverEl: HTMLElement | null = null;
let onDocDown: ((e: MouseEvent) => void) | null = null;
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;

function closePopover() {
  popoverEl?.remove();
  popoverEl = null;
  if (onDocDown) {
    document.removeEventListener("mousedown", onDocDown, true);
    onDocDown = null;
  }
  if (onKeyDown) {
    document.removeEventListener("keydown", onKeyDown, true);
    onKeyDown = null;
  }
}

function showPopover(
  anchor: HTMLElement,
  suggestions: string[],
  handlers: { onPick: (s: string) => void; onIgnore: () => void }
) {
  closePopover();
  const rect = anchor.getBoundingClientRect();
  const el = document.createElement("div");
  el.setAttribute("role", "menu");
  el.setAttribute("aria-label", "Spelling suggestions");
  el.setAttribute(
    "style",
    [
      "position:fixed",
      // Provisional position; clamped to the viewport after measuring (below).
      `left:${rect.left}px`,
      `top:${rect.bottom + 4}px`,
      "z-index:1000",
      "min-width:168px",
      "max-width:260px",
      "padding:4px",
      "background:#fff",
      "border:1px solid #e2e5eb",
      "border-radius:8px",
      "box-shadow:0 4px 16px rgba(26,25,57,0.14)",
      "font-size:13px",
    ].join(";")
  );

  const itemStyle =
    "display:block;width:100%;padding:6px 10px;border:none;border-radius:6px;background:transparent;text-align:left;font-size:13px;cursor:pointer;";

  if (suggestions.length === 0) {
    const none = document.createElement("div");
    none.setAttribute("style", "padding:6px 10px;color:#8a8a98;");
    none.textContent = "No suggestions";
    el.appendChild(none);
  } else {
    for (const s of suggestions) {
      const b = document.createElement("button");
      b.type = "button";
      b.setAttribute("role", "menuitem");
      b.setAttribute("style", `${itemStyle}font-weight:600;color:#1a1939;`);
      b.textContent = s;
      b.addEventListener("mouseenter", () => (b.style.background = "#f0f7ff"));
      b.addEventListener("mouseleave", () => (b.style.background = "transparent"));
      // mousedown only guards against the editor stealing focus / placing a
      // caret; the action fires on click so keyboard (Enter/Space) works too.
      b.addEventListener("mousedown", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      });
      b.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        handlers.onPick(s);
        closePopover();
      });
      el.appendChild(b);
    }
  }

  const ignore = document.createElement("button");
  ignore.type = "button";
  ignore.setAttribute("role", "menuitem");
  ignore.setAttribute(
    "style",
    `${itemStyle}margin-top:4px;border-top:1px solid #eef1f6;color:#8a8a98;`
  );
  ignore.textContent = "Ignore";
  ignore.addEventListener("mousedown", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
  });
  ignore.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    handlers.onIgnore();
    closePopover();
  });
  el.appendChild(ignore);

  document.body.appendChild(el);
  popoverEl = el;

  // Clamp within the viewport now that the popover has a measurable size, so it
  // never opens off-screen for a squiggle near the right/bottom edge.
  const margin = 8;
  const box = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = rect.left;
  let top = rect.bottom + 4;
  if (left + box.width > vw - margin)
    left = Math.max(margin, vw - margin - box.width);
  if (top + box.height > vh - margin) {
    // Not enough room below the word — flip above it if that fits better.
    const above = rect.top - 4 - box.height;
    top = above >= margin ? above : Math.max(margin, vh - margin - box.height);
  }
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;

  // Move keyboard focus into the menu and support arrow / Escape navigation.
  const items = Array.from(el.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  items[0]?.focus();
  onKeyDown = (e: KeyboardEvent) => {
    if (!popoverEl) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closePopover();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (items.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? items.indexOf(active) : -1;
      const dir = e.key === "ArrowDown" ? 1 : -1;
      const next = (idx + dir + items.length) % items.length;
      items[next]?.focus();
    }
  };
  document.addEventListener("keydown", onKeyDown, true);

  onDocDown = (e: MouseEvent) => {
    if (popoverEl && !popoverEl.contains(e.target as Node)) closePopover();
  };
  window.setTimeout(
    () => onDocDown && document.addEventListener("mousedown", onDocDown, true),
    0
  );
}
